import { useState, useCallback, useRef } from 'react';
import { requestUploadUrl, confirmUpload } from '../api/uploads.js';

const ALLOWED_TYPES = new Set([
  'image/jpeg', 'image/png', 'image/webp', 'image/gif',
  'audio/mpeg', 'audio/wav', 'audio/flac', 'audio/aac', 'audio/ogg',
]);

const MAX_CONCURRENT = 3;

let fileIdCounter = 0;

export function useUpload() {
  const [files, setFiles] = useState([]);
  const activeUploads = useRef(0);
  const queue = useRef([]);

  const addFiles = useCallback((fileList) => {
    const newFiles = Array.from(fileList)
      .filter((f) => ALLOWED_TYPES.has(f.type))
      .map((f) => ({
        id: `file-${++fileIdCounter}`,
        file: f,
        name: f.name,
        type: f.type,
        size: f.size,
        progress: 0,
        status: 'pending',
        error: null,
        assetId: null,
      }));

    setFiles((prev) => [...prev, ...newFiles]);
    return newFiles;
  }, []);

  const updateFile = useCallback((id, updates) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  }, []);

  const uploadSingle = useCallback(async (fileEntry, projectId) => {
    const { id, file } = fileEntry;

    try {
      // Step 1: Request upload URL
      updateFile(id, { status: 'requesting' });
      const { asset_id, upload_url } = await requestUploadUrl(projectId, file.name, file.type);
      updateFile(id, { assetId: asset_id });

      // Step 2: Upload to GCS via XHR for progress
      updateFile(id, { status: 'uploading' });
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            updateFile(id, { progress: Math.round((e.loaded / e.total) * 100) });
          }
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
      });

      // Step 3: Confirm upload
      updateFile(id, { status: 'confirming', progress: 100 });
      const confirmed = await confirmUpload(projectId, asset_id);
      updateFile(id, { status: 'confirmed', assetId: confirmed.id });
    } catch (err) {
      updateFile(id, { status: 'error', error: err.message || 'Upload failed' });
    }
  }, [updateFile]);

  const processQueue = useCallback((projectId) => {
    while (activeUploads.current < MAX_CONCURRENT && queue.current.length > 0) {
      const fileEntry = queue.current.shift();
      activeUploads.current++;
      uploadSingle(fileEntry, projectId).finally(() => {
        activeUploads.current--;
        processQueue(projectId);
      });
    }
  }, [uploadSingle]);

  const startUpload = useCallback((projectId) => {
    setFiles((prev) => {
      const pending = prev.filter((f) => f.status === 'pending');
      queue.current.push(...pending);
      processQueue(projectId);
      return prev;
    });
  }, [processQueue]);

  const removeFile = useCallback((id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    queue.current = queue.current.filter((f) => f.id !== id);
  }, []);

  const retryFile = useCallback((id) => {
    updateFile(id, { status: 'pending', progress: 0, error: null });
  }, [updateFile]);

  const isUploading = files.some((f) => ['requesting', 'uploading', 'confirming'].includes(f.status));

  return { files, addFiles, startUpload, removeFile, retryFile, isUploading };
}
